/**
 * CRMPage - Full Featured Version
 * Features: All leads visible, editable remarks, date range filter, Meta Ads source, follow-ups tab
 */
import { useState, useMemo, useRef, useEffect } from 'react';
import { crmAPI, employeeAPI, exportAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useApi, useMutation } from '../hooks/useApi';
import { useTable } from '../hooks/useTable';
import { formatDateTime, getInitials } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '../components/ui/sheet';
import {
  Alert, AlertDescription, AlertTitle
} from '../components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { 
  Plus, Loader2, Phone, PhoneCall, Mail, MessageCircle, Calendar, FileText, X, Users, 
  TrendingUp, Award, Upload, Download, AlertTriangle, 
  Clock, Bell, RefreshCw, Trash2, Edit, CalendarRange, Check, Sparkles, RotateCcw, XCircle, IndianRupee, DollarSign, BarChart3, Columns3, Facebook
} from 'lucide-react';

// Shared components
import { DataTable } from '../components/shared/DataTable';
import ExportButton from '../components/ExportButton';
import { SearchInput } from '../components/shared/SearchInput';
import { PageSkeleton } from '../components/shared/LoadingSkeleton';
import { Pagination } from '../components/shared/Pagination';
import CRMKanbanBoard from '../components/CRMKanbanBoard';
import CRMAnalytics from '../components/CRMAnalytics';
import AICallDialog from '../components/crm/AICallDialog';
import MetaLeadsTab from '../components/crm/MetaLeadsTab';
import WhatsAppMessenger from '../components/WhatsAppMessenger';

// Constants
const STATUSES = [
  'new', 'contacted', 'qualified', 
  'follow_up_1', 'follow_up_2', 'follow_up_3', 
  'not_responded',
  'proposal', 'proposal_followup_1', 'proposal_followup_2', 'proposal_followup_3',
  'won', 'lost'
];

const SOURCES = ['Website', 'Referral', 'LinkedIn', 'Cold Call', 'Event', 'WhatsApp', 'Meta-US', 'Meta-India', 'USA Lead', 'Other'];

const DOMAINS = [
  'Digital Marketing',
  'Web Development', 
  'Mobile App Development',
  'Cloud Services',
  'IT Consulting',
  'E-commerce',
  'SaaS',
  'Enterprise Software',
  'Other'
];

const ACTIVITY_TYPES = [
  { value: 'call', label: 'Call', icon: Phone },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { value: 'meeting', label: 'Meeting', icon: Calendar },
  { value: 'note', label: 'Note', icon: FileText },
  { value: 'follow_up', label: 'Follow Up', icon: Bell },
];

const STATUS_COLORS = {
  new: 'secondary', contacted: 'outline', qualified: 'outline',
  follow_up_1: 'default', follow_up_2: 'default', follow_up_3: 'default',
  not_responded: 'destructive',
  proposal: 'default', 
  proposal_followup_1: 'default', proposal_followup_2: 'default', proposal_followup_3: 'default',
  won: 'default', lost: 'destructive',
};

const STATUS_LABELS = {
  new: 'New', contacted: 'Contacted', qualified: 'Qualified',
  follow_up_1: 'Follow Up 1', follow_up_2: 'Follow Up 2', follow_up_3: 'Follow Up 3',
  not_responded: 'Not Responded',
  proposal: 'Proposal',
  proposal_followup_1: 'Proposal - Follow Up 1',
  proposal_followup_2: 'Proposal - Follow Up 2',
  proposal_followup_3: 'Proposal - Follow Up 3',
  won: 'Won', lost: 'Lost',
};

const FOLLOWUP_RULES = {
  follow_up_1: '3-day aging window',
  follow_up_2: '3-day aging window',
  follow_up_3: '7 working-day aging window',
};

const formatCurrency = (val) => {
  if (!val && val !== 0) return '-';
  const num = Number(val);
  if (isNaN(num)) return '-';
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(1)}Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(1)}L`;
  if (num >= 1000) return `₹${(num / 1000).toFixed(1)}K`;
  return `₹${num.toLocaleString('en-IN')}`;
};

const defaultLead = { 
  name: '', company: '', email: '', phone: '', 
  source: 'Website', status: 'new', domain: '', 
  owner: '', notes: '', remarks: '', estimated_deal_size: '' 
};
const defaultActivity = { activity_type: 'call', description: '', next_follow_up: '' };

export default function CRMPage() {
  const { user: _user, isSuperAdmin, isAdmin, hasModuleAccess } = useAuth();
  const canValidateLeads = hasModuleAccess('meta_connect');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [formData, setFormData] = useState(defaultLead);
  const [editFormData, setEditFormData] = useState(defaultLead);
  const [activityForm, setActivityForm] = useState(defaultActivity);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [domainFilter, setDomainFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('all');
  const [editingRemarks, setEditingRemarks] = useState(null);
  const [remarksValue, setRemarksValue] = useState('');
  const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  
  // Date range filter for leads - split into input state and filter state
  const [dateFromInput, setDateFromInput] = useState('');  // What user types
  const [dateToInput, setDateToInput] = useState('');      // What user types
  const [dateFrom, setDateFrom] = useState('');            // What triggers API
  const [dateTo, setDateTo] = useState('');                // What triggers API
  
  // Debounce date filter changes to avoid loading on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDateFrom(dateFromInput);
      setDateTo(dateToInput);
    }, 500); // Wait 500ms after user stops typing
    
    return () => clearTimeout(timer);
  }, [dateFromInput, dateToInput]);
  
  // Date range filter for follow-ups
  const [followUpDateFrom, setFollowUpDateFrom] = useState('');
  const [followUpDateTo, setFollowUpDateTo] = useState('');
  
  // Import state
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Aging alerts state
  const [sendingAlerts, setSendingAlerts] = useState(false);
  
  // WhatsApp modal state
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [selectedLeadForWhatsApp, setSelectedLeadForWhatsApp] = useState(null);
  
  // AI Call state
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [callLead, setCallLead] = useState(null);
  
  // Follow-ups state
  const [followUps, setFollowUps] = useState([]);
  const [followUpsSummary, setFollowUpsSummary] = useState(null);
  const [loadingFollowUps, setLoadingFollowUps] = useState(false);

  // Data fetching - refresh when date filters change
  const { data: leads, loading, refresh } = useApi(
    () => crmAPI.getLeads({ 
      date_from: dateFrom || undefined, 
      date_to: dateTo || undefined 
    }), 
    { deps: [dateFrom, dateTo] }
  );
  const { data: employees } = useApi(employeeAPI.getAll);
  const { data: agingSummary, refresh: refreshAging } = useApi(crmAPI.getAgingSummary);
  const { data: dashboardStats, refresh: refreshDashboard } = useApi(crmAPI.getDashboard);
  
  // Deleted leads state (Super Admin only)
  const [deletedLeads, setDeletedLeads] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);

  // Fetch follow-ups when tab changes or date filters change
  useEffect(() => {
    if (activeTab === 'follow_ups') {
      loadFollowUps();
    }
    if (activeTab === 'deleted' && isSuperAdmin) {
      loadDeletedLeads();
    }
  }, [activeTab, isSuperAdmin, followUpDateFrom, followUpDateTo]);
  
  const loadDeletedLeads = async () => {
    setLoadingDeleted(true);
    try {
      const res = await crmAPI.getDeletedLeads();
      setDeletedLeads(res.data || []);
    } catch (error) {
      console.error('Failed to load deleted leads:', error);
    } finally {
      setLoadingDeleted(false);
    }
  };
  
  const handleRestoreLead = async (leadId) => {
    try {
      await crmAPI.restoreLead(leadId);
      toast.success('Lead restored successfully');
      loadDeletedLeads();
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore lead');
    }
  };

  const loadFollowUps = async () => {
    setLoadingFollowUps(true);
    try {
      // Build params for date filtering
      const params = {};
      if (followUpDateFrom) params.date_from = followUpDateFrom;
      if (followUpDateTo) params.date_to = followUpDateTo;
      
      const [fups, summary] = await Promise.all([
        crmAPI.getMyFollowUps(params),
        crmAPI.getFollowUpsSummary()
      ]);
      setFollowUps(fups.data?.follow_ups || []);
      setFollowUpsSummary(summary.data);
    } catch (error) {
      console.error('Failed to load follow-ups:', error);
    } finally {
      setLoadingFollowUps(false);
    }
  };

  // Mutations
  const { mutate: saveLead, loading: creating } = useMutation(crmAPI.createLead, {
    successMessage: 'Lead added',
    onSuccess: () => { setDialogOpen(false); setFormData(defaultLead); refresh(); refreshAging(); refreshDashboard(); }
  });

  // Table hook
  const filteredByStatus = useMemo(() => {
    let filtered = leads || [];
    
    if (activeTab === 'aging_3') {
      return agingSummary?.leads_3_days || [];
    } else if (activeTab === 'aging_7') {
      return agingSummary?.leads_7_days || [];
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(l => l.status === statusFilter);
    }
    if (sourceFilter !== 'all') {
      filtered = filtered.filter(l => l.source === sourceFilter);
    }
    if (domainFilter !== 'all') {
      filtered = filtered.filter(l => l.domain === domainFilter);
    }
    if (assigneeFilter === '__me__') {
      // "My leads" — leads assigned to the logged-in user (match by name or id)
      filtered = filtered.filter(l =>
        (_user?.name && l.assigned_to_name === _user.name) ||
        (_user?.id && (l.assigned_to === _user.id || l.owner === _user.id))
      );
    } else if (assigneeFilter !== 'all') {
      // Filter by assigned_to_name since assigned_to IDs may be outdated
      const selectedEmployee = (employees || []).find(e => e.id === assigneeFilter);
      if (selectedEmployee) {
        filtered = filtered.filter(l => l.assigned_to_name === selectedEmployee.name);
      }
    }
    return filtered;
  }, [leads, statusFilter, sourceFilter, domainFilter, assigneeFilter, activeTab, agingSummary, _user]);

  const { items, search, setSearch, sort, setSort, pagination } = useTable(filteredByStatus, {
    searchFields: ['name', 'company', 'email', 'phone', 'remarks'],
    defaultSort: { field: 'created_at', order: 'desc' },
    pageSize: 25
  });

  // Stats
  const stats = useMemo(() => ({
    total: (leads || []).length,
    new: (leads || []).filter(l => l.status === 'new').length,
    qualified: (leads || []).filter(l => l.status === 'qualified').length,
    follow_ups: (leads || []).filter(l => ['follow_up_1', 'follow_up_2', 'follow_up_3'].includes(l.status)).length,
    won: (leads || []).filter(l => l.status === 'won').length,
    lost: (leads || []).filter(l => l.status === 'lost').length,
    conversionRate: (leads || []).length > 0 
      ? (((leads || []).filter(l => l.status === 'won').length / (leads || []).length) * 100).toFixed(1)
      : 0,
    aging3Days: agingSummary?.total_aging_3_days || 0,
    aging7Days: agingSummary?.total_aging_7_days || 0,
    pipeline: dashboardStats?.pipeline || { total_value: 0, active_value: 0, won_value: 0, avg_deal_size: 0, conversion_rate: 0 },
  }), [leads, agingSummary, dashboardStats]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!formData.remarks || formData.remarks.trim().length < 3) {
      toast.error('Please add a meaningful remark (at least 3 characters)');
      return;
    }
    const payload = { ...formData };
    if (payload.estimated_deal_size !== '' && payload.estimated_deal_size !== null) {
      payload.estimated_deal_size = Number(payload.estimated_deal_size);
    } else {
      payload.estimated_deal_size = null;
    }
    await saveLead(payload);
  };

  const handleLeadClick = async (lead) => {
    setSelectedLead(lead);
    setDetailOpen(true);
    try {
      const res = await crmAPI.getActivities(lead.id);
      setActivities(res.data || []);
    } catch (error) {
      setActivities([]);
    }
  };

  // Open edit dialog
  const handleEditLead = (lead, e) => {
    if (e) e.stopPropagation();
    setEditFormData({
      name: lead.name || '',
      company: lead.company || '',
      email: lead.email || '',
      phone: lead.phone || '',
      source: lead.source || 'Website',
      status: lead.status || 'new',
      domain: lead.domain || '',
      owner: lead.assigned_to || '',
      notes: lead.notes || '',
      remarks: lead.remarks || '',
      estimated_deal_size: lead.estimated_deal_size || ''
    });
    setSelectedLead(lead);
    setEditDialogOpen(true);
  };

  // Submit edit
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLead) return;
    if (!editFormData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!editFormData.remarks || editFormData.remarks.trim().length < 3) {
      toast.error('Please add a meaningful remark (at least 3 characters)');
      return;
    }
    try {
      const dealSize = editFormData.estimated_deal_size !== '' && editFormData.estimated_deal_size !== null 
        ? Number(editFormData.estimated_deal_size) : null;
      await crmAPI.updateLead(selectedLead.id, {
        name: editFormData.name,
        company: editFormData.company,
        email: editFormData.email,
        phone: editFormData.phone,
        source: editFormData.source,
        status: editFormData.status,
        domain: editFormData.domain,  // Add domain field
        assigned_to: editFormData.owner,
        notes: editFormData.notes,
        remarks: editFormData.remarks,
        estimated_deal_size: dealSize
      });
      toast.success('Lead updated successfully');
      setEditDialogOpen(false);
      setDetailOpen(false);
      refresh();
      refreshAging();
      refreshDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update lead');
    }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    if (!selectedLead) return;
    try {
      const payload = {
        activity_type: activityForm.activity_type,
        description: activityForm.description || '',
        next_follow_up: activityForm.next_follow_up ? new Date(activityForm.next_follow_up).toISOString() : null
      };
      await crmAPI.createActivity(selectedLead.id, payload);
      toast.success('Activity logged');
      setActivityDialogOpen(false);
      setActivityForm(defaultActivity);
      const res = await crmAPI.getActivities(selectedLead.id);
      setActivities(res.data || []);
      // Refresh follow-ups if on that tab
      if (activeTab === 'follow_ups') {
        loadFollowUps();
      }
    } catch (error) {
      console.error('Activity error:', error);
      toast.error(error.response?.data?.detail || 'Failed to add activity');
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await crmAPI.updateLead(leadId, { status: newStatus });
      toast.success('Status updated');
      refresh();
      refreshAging();
      refreshDashboard();
      if (selectedLead?.id === leadId) {
        setSelectedLead({ ...selectedLead, status: newStatus });
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Bulk selection
  const toggleSelectLead = (id) => {
    setSelectedLeadIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    const currentLeads = filteredByStatus || [];
    if (selectedLeadIds.size === currentLeads.length) {
      setSelectedLeadIds(new Set());
    } else {
      setSelectedLeadIds(new Set(currentLeads.map(l => l.id)));
    }
  };
  const handleBulkAction = async () => {
    if (!bulkAction || selectedLeadIds.size === 0) return;
    const ids = Array.from(selectedLeadIds);
    let successCount = 0;
    for (const id of ids) {
      try {
        if (bulkAction.startsWith('status:')) {
          await crmAPI.updateLead(id, { status: bulkAction.replace('status:', '') });
        } else if (bulkAction.startsWith('assign:')) {
          await crmAPI.updateLead(id, { assigned_to: bulkAction.replace('assign:', '') });
        }
        successCount++;
      } catch { /* skip failed */ }
    }
    toast.success(`Updated ${successCount} of ${ids.length} leads`);
    setSelectedLeadIds(new Set());
    setBulkAction('');
    refresh();
    refreshAging();
    refreshDashboard();
  };

  // Remarks inline edit
  const handleRemarksEdit = (lead) => {
    setEditingRemarks(lead.id);
    setRemarksValue(lead.remarks || '');
  };

  const handleRemarksSave = async (leadId) => {
    if (!remarksValue || remarksValue.trim().length < 3) {
      toast.error('Please add a meaningful remark (at least 3 characters)');
      return;
    }
    try {
      await crmAPI.updateLead(leadId, { remarks: remarksValue });
      toast.success('Remarks updated');
      setEditingRemarks(null);
      refresh();
    } catch (error) {
      toast.error('Failed to update remarks');
    }
  };

  const handleRemarksCancel = () => {
    setEditingRemarks(null);
    setRemarksValue('');
  };

  // Delete lead
  const handleDeleteLead = async () => {
    if (!selectedLead) return;
    try {
      await crmAPI.deleteLead(selectedLead.id);
      toast.success('Lead deleted');
      setDeleteDialogOpen(false);
      setDetailOpen(false);
      setSelectedLead(null);
      refresh();
      refreshAging();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete lead');
    }
  };

  // Import handling
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await crmAPI.importLeads(formData);
      setImportResult(res.data);
      toast.success(`Imported ${res.data.imported} leads`);
      refresh();
      refreshAging();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import leads');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await crmAPI.getImportTemplate();
      const template = res.data;
      
      // Create CSV content
      const headers = template.columns.map(c => c.name).join(',');
      const sampleRow = template.columns.map(c => template.sample_data[0][c.name] || '').join(',');
      const csvContent = `${headers}\n${sampleRow}`;
      
      // Download
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'lead_import_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // Send aging alerts manually
  const handleSendAgingAlerts = async () => {
    if (!confirm('Send aging alert emails to assignees and admins?')) return;
    
    setSendingAlerts(true);
    try {
      const res = await crmAPI.triggerAgingAlerts();
      toast.success(`Sent ${res.data.sent} aging alert emails`);
    } catch (error) {
      toast.error('Failed to send alerts');
    } finally {
      setSendingAlerts(false);
    }
  };

  // Clear date filter
  const clearDateFilter = () => {
    setDateFromInput('');
    setDateToInput('');
    setDateFrom('');
    setDateTo('');
  };

  // Table columns
  const columns = [
    { key: '_select', label: () => (
      <input type="checkbox" checked={selectedLeadIds.size > 0 && selectedLeadIds.size === (filteredByStatus || []).length} onChange={toggleSelectAll} className="accent-primary w-4 h-4 cursor-pointer" data-testid="select-all-leads" />
    ), render: (row) => (
      <input type="checkbox" checked={selectedLeadIds.has(row.id)} onChange={() => toggleSelectLead(row.id)} className="accent-primary w-4 h-4 cursor-pointer" data-testid={`select-lead-${row.id}`} />
    )},
    { key: 'name', label: 'Lead', sortable: true, render: (row) => (
      <div className="flex items-center gap-3">
        <Avatar className="h-9 w-9">
          <AvatarFallback className="bg-primary/10 text-primary text-sm">{getInitials(row.name)}</AvatarFallback>
        </Avatar>
        <div>
          <p className="font-medium">{row.name}</p>
          <p className="text-sm text-muted-foreground">{row.company}</p>
        </div>
      </div>
    )},
    { key: 'email', label: 'Contact', render: (row) => (
      <div className="text-sm">
        <p>{row.email || '-'}</p>
        <p className="text-muted-foreground">{row.phone || '-'}</p>
      </div>
    )},
    { key: 'source', label: 'Source', render: (row) => <Badge variant="outline">{row.source || '-'}</Badge> },
    { key: 'domain', label: 'Domain', render: (row) => (
      row.domain ? <Badge variant="secondary" className="text-xs">{row.domain}</Badge> : <span className="text-muted-foreground text-xs">-</span>
    )},
    { key: 'estimated_deal_size', label: 'Deal Size', sortable: true, render: (row) => (
      <span className={`font-medium ${row.estimated_deal_size ? 'text-emerald-700' : 'text-muted-foreground'}`}>
        {formatCurrency(row.estimated_deal_size)}
      </span>
    )},
    { key: 'lead_score', label: 'Score', sortable: true, render: (row) => {
      const score = row.lead_score || 0;
      const grade = row.lead_grade || 'F';
      const bg = grade === 'A' ? 'bg-green-600' : grade === 'B' ? 'bg-blue-600' : grade === 'C' ? 'bg-yellow-600' : grade === 'D' ? 'bg-orange-600' : 'bg-red-600';
      const gradeInfo = {
        A: { label: 'Hot Lead', desc: 'High priority — ready to convert. Follow up immediately.' },
        B: { label: 'Warm Lead', desc: 'Good potential. Nurture with targeted follow-ups.' },
        C: { label: 'Moderate', desc: 'Needs engagement. Schedule a discovery call.' },
        D: { label: 'Cool Lead', desc: 'Low engagement. Re-qualify or deprioritize.' },
        F: { label: 'Cold Lead', desc: 'Minimal activity. Consider archiving or re-targeting.' },
      }[grade] || { label: grade, desc: '' };
      return (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge className={`${bg} text-white text-[10px] font-bold cursor-help`} data-testid={`lead-score-${row.id}`}>
                {grade} {score}
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[280px] p-3 bg-slate-900 text-white rounded-lg shadow-xl">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-sm">{gradeInfo.label}</span>
                  <span className="text-xs opacity-80">{score}/100</span>
                </div>
                <p className="text-xs leading-relaxed opacity-90">{gradeInfo.desc}</p>
                {row.score_factors?.length > 0 && (
                  <div className="border-t border-white/20 pt-2 mt-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide opacity-70 mb-1">Score Breakdown</p>
                    <ul className="space-y-0.5">
                      {row.score_factors.map((f, i) => (
                        <li key={i} className="text-[11px] opacity-85 flex items-start gap-1">
                          <span className="shrink-0 mt-0.5">•</span>{f}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="border-t border-white/20 pt-1.5 mt-1.5">
                  <p className="text-[10px] opacity-60">A (80-100) &gt; B (60-79) &gt; C (40-59) &gt; D (20-39) &gt; F (0-19)</p>
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }},
    { key: 'status', label: 'Status', render: (row) => (
      <Select value={row.status} onValueChange={(v) => handleStatusChange(row.id, v)}>
        <SelectTrigger className="w-[140px] h-8">
          <Badge variant={STATUS_COLORS[row.status] || 'outline'}>{STATUS_LABELS[row.status] || row.status}</Badge>
        </SelectTrigger>
        <SelectContent>
          {STATUSES.map(s => <SelectItem key={s} value={s}>{STATUS_LABELS[s] || s}</SelectItem>)}
        </SelectContent>
      </Select>
    )},
    { key: 'remarks', label: 'Remarks', render: (row) => (
      editingRemarks === row.id ? (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Input 
            value={remarksValue} 
            onChange={(e) => setRemarksValue(e.target.value)}
            className="h-8 w-40"
            placeholder="Add remarks..."
            autoFocus
          />
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => handleRemarksSave(row.id)}>
            <Check className="w-4 h-4 text-green-600" />
          </Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={handleRemarksCancel}>
            <X className="w-4 h-4 text-red-600" />
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-1 group cursor-pointer" onClick={(e) => { e.stopPropagation(); handleRemarksEdit(row); }}>
          <span className="text-sm text-muted-foreground max-w-32 truncate">
            {row.remarks || <span className="italic text-gray-400">Add remarks</span>}
          </span>
          <Edit className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100" />
        </div>
      )
    )},
    { key: 'aging_days', label: 'Aging', render: (row) => {
      const days = row.aging_days || 0;  // Use new aging_days (based on follow-up overdue)
      const status = row.status || '';
      
      // Hide aging for completed stages (won, lost, proposal stages, qualified)
      const completedStatuses = ['won', 'lost', 'proposal', 'proposal_followup_1', 'proposal_followup_2', 'proposal_followup_3', 'qualified'];
      if (completedStatuses.includes(status)) {
        return <span className="text-muted-foreground">-</span>;
      }
      
      // If no follow-up scheduled or not overdue yet, show 0 or -
      if (days === 0) {
        return <span className="text-muted-foreground">-</span>;
      }
      
      const isFollowUp = status.startsWith('follow_up_');
      const rule = FOLLOWUP_RULES[status];
      
      if (isFollowUp) {
        const threshold = status === 'follow_up_3' ? 7 : 3;
        const label = status === 'follow_up_3' ? 'work days' : 'days';
        if (days >= threshold) return <Badge variant="destructive" title={rule}>{days}d / {threshold}{status === 'follow_up_3' ? 'wd' : 'd'}</Badge>;
        return <span className="text-muted-foreground" title={rule}>{days}d / {threshold}{status === 'follow_up_3' ? 'wd' : 'd'}</span>;
      }
      if (days >= 7) return <Badge variant="destructive">{days} days</Badge>;
      if (days >= 3) return <Badge variant="outline" className="text-orange-600 border-orange-300">{days} days</Badge>;
      return <span className="text-muted-foreground">{days} days</span>;
    }},
    { key: 'assigned_to_name', label: 'Assignee', render: (row) => row.assigned_to_name || '-' },
    { key: 'created_at', label: 'Created', sortable: true, render: (row) => formatDateTime(row.created_at).split(',')[0] },
    { key: 'actions', label: 'Actions', render: (row) => (
      <div className="flex items-center gap-1">
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                onClick={(e) => { e.stopPropagation(); setCallLead(row); setCallDialogOpen(true); }}
                data-testid={`ai-call-lead-${row.id}`}
              >
                <PhoneCall className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top"><p>AI Voice Call</p></TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={(e) => handleEditLead(row, e)}
          data-testid={`edit-lead-${row.id}`}
        >
          <Edit className="w-4 h-4 mr-1" /> Edit
        </Button>
      </div>
    )},
  ];

  // Follow-ups columns
  const followUpColumns = [
    { key: 'lead_name', label: 'Lead', render: (row) => (
      <div>
        <p className="font-medium">{row.lead_name}</p>
        <p className="text-sm text-muted-foreground">{row.lead_company || '-'}</p>
      </div>
    )},
    { key: 'lead_status', label: 'Status', render: (row) => (
      <div className="flex flex-col gap-1">
        <Badge variant={STATUS_COLORS[row.lead_status] || 'outline'}>{STATUS_LABELS[row.lead_status] || row.lead_status}</Badge>
        {row.source === 'rule' && <span className="text-[10px] text-indigo-500 font-medium">Rule-based</span>}
      </div>
    )},
    { key: 'activity_type', label: 'Type', render: (row) => {
      const type = ACTIVITY_TYPES.find(t => t.value === row.activity_type);
      return <Badge variant="outline">{type?.label || row.activity_type}</Badge>;
    }},
    { key: 'description', label: 'Notes', render: (row) => <span className="text-sm max-w-48 truncate block">{row.description || '-'}</span> },
    { key: 'next_follow_up', label: 'Follow Up Date', render: (row) => {
      const date = row.next_follow_up ? new Date(row.next_follow_up) : null;
      if (!date) return '-';
      const formatted = date.toLocaleDateString();
      return row.is_overdue 
        ? <Badge variant="destructive">{formatted} (Overdue)</Badge>
        : <span>{formatted}</span>;
    }},
    { key: 'created_by_name', label: 'Created By', render: (row) => row.created_by_name || '-' },
  ];

  // Show full skeleton only on initial load (not on filter changes)
  const [initialLoad, setInitialLoad] = useState(true);
  
  useEffect(() => {
    if (!loading && initialLoad) {
      setInitialLoad(false);
    }
  }, [loading, initialLoad]);
  
  if (loading && initialLoad) return <PageSkeleton />;

  return (
    <div className="space-y-6 animate-fadeIn max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']" data-testid="crm-page-title">CRM</h1>
          <p className="text-muted-foreground mt-1">Manage leads and customer relationships</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Export Button */}
          <ExportButton 
            exportFn={exportAPI.leads} 
            filename="crm_leads.csv" 
            label="Export"
          />
          
          {/* Import Button */}
          <Button variant="outline" onClick={() => setImportDialogOpen(true)} data-testid="import-leads-btn">
            <Upload className="w-4 h-4 mr-2" /> Import
          </Button>
          
          {/* Add Lead Button */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="add-lead-btn"><Plus className="w-4 h-4 mr-2" /> Add Lead</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Lead</DialogTitle>
                <DialogDescription>Enter lead information</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name <span className="text-red-500">*</span></Label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="lead-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Company</Label>
                    <Input value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} data-testid="lead-company-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} data-testid="lead-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="lead-phone-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Source</Label>
                    <Select value={formData.source} onValueChange={(v) => setFormData({ ...formData, source: v })}>
                      <SelectTrigger data-testid="lead-source-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Domain</Label>
                    <Select value={formData.domain || ''} onValueChange={(v) => setFormData({ ...formData, domain: v })}>
                      <SelectTrigger data-testid="lead-domain-select"><SelectValue placeholder="Select domain" /></SelectTrigger>
                      <SelectContent>{DOMAINS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Assignee</Label>
                    <Select value={formData.owner} onValueChange={(v) => setFormData({ ...formData, owner: v })}>
                      <SelectTrigger data-testid="lead-owner-select"><SelectValue placeholder="Select assignee" /></SelectTrigger>
                      <SelectContent>{(employees || []).map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Estimated Deal Size (₹)</Label>
                    <Input type="number" min="0" step="1000" value={formData.estimated_deal_size} onChange={(e) => setFormData({ ...formData, estimated_deal_size: e.target.value })} placeholder="e.g. 500000" data-testid="lead-deal-size-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Remarks <span className="text-red-500">*</span></Label>
                  <Input value={formData.remarks} onChange={(e) => setFormData({ ...formData, remarks: e.target.value })} placeholder="Add remarks (required)..." data-testid="lead-remarks-input" required minLength={3} />
                  {formData.remarks.length > 0 && formData.remarks.trim().length < 3 && (
                    <p className="text-xs text-red-500">Remark must be at least 3 characters</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} data-testid="lead-notes-input" />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={creating} data-testid="save-lead-btn">{creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Lead'}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Lead Aging Alert Banner */}
      {(stats.aging7Days > 0) && (
        <Alert variant="destructive" className="bg-red-100 border-red-300">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertTitle className="text-red-800 font-semibold">Aging Leads Alert</AlertTitle>
          <AlertDescription className="flex items-center justify-between text-red-700">
            <span>
              {stats.aging7Days} lead(s) have not been updated in 7+ days.
              {stats.aging3Days > 0 && ` ${stats.aging3Days} additional lead(s) aging 3-6 days.`}
            </span>
            {(isSuperAdmin || isAdmin) && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleSendAgingAlerts}
                disabled={sendingAlerts}
                className="border-red-400 text-red-700 hover:bg-red-200"
                data-testid="send-aging-alerts-btn"
              >
                {sendingAlerts ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Bell className="w-4 h-4 mr-1" />}
                Send Alerts
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Stats - responsive grid, no horizontal scroll */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9 gap-2">
        <StatTile label="Total" value={stats.total} icon={Users} onClick={() => { setActiveTab('all'); setStatusFilter('all'); }} testId="stat-total" />
        <StatTile label="New" value={stats.new} color="text-blue-600" onClick={() => { setActiveTab('all'); setStatusFilter('new'); }} testId="stat-new" />
        <StatTile label="Qualified" value={stats.qualified} color="text-yellow-600" onClick={() => { setActiveTab('all'); setStatusFilter('qualified'); }} testId="stat-qualified" />
        <StatTile label="Follow Ups" value={stats.follow_ups} icon={Bell} color="text-indigo-600" bg="bg-indigo-50/40" onClick={() => { setActiveTab('all'); setStatusFilter('follow_up_1'); }} testId="stat-followups" />
        <StatTile label="Won" value={stats.won} icon={Award} color="text-green-600" bg="bg-green-50/40" onClick={() => { setActiveTab('all'); setStatusFilter('won'); }} testId="stat-won" />
        <StatTile label="Lost" value={stats.lost} icon={XCircle} color="text-red-600" bg="bg-red-50/40" onClick={() => { setActiveTab('all'); setStatusFilter('lost'); }} testId="stat-lost" />
        <StatTile label="3+ Days" value={stats.aging3Days} icon={Clock} color="text-orange-600" onClick={() => setActiveTab('aging_3')} testId="stat-aging-3" />
        <StatTile label="7+ Days" value={stats.aging7Days} icon={AlertTriangle} color="text-red-600" onClick={() => setActiveTab('aging_7')} testId="stat-aging-7" />
        <StatTile label="Conv." value={`${stats.conversionRate}%`} icon={TrendingUp} clickable={false} testId="stat-conversion" />
      </div>

      {/* Pipeline Value - responsive grid, no horizontal scroll */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="rounded-lg border bg-emerald-50/50 border-emerald-200 px-3 py-2" data-testid="pipeline-active">
          <div className="flex items-center gap-1 text-emerald-600 text-[11px] mb-0.5"><IndianRupee className="w-3 h-3" />Active Pipeline</div>
          <p className="text-lg font-bold text-emerald-700">{formatCurrency(stats.pipeline.active_value)}</p>
        </div>
        <div className="rounded-lg border bg-green-50/50 border-green-200 px-3 py-2" data-testid="pipeline-won">
          <div className="flex items-center gap-1 text-green-600 text-[11px] mb-0.5"><Award className="w-3 h-3" />Won Revenue</div>
          <p className="text-lg font-bold text-green-700">{formatCurrency(stats.pipeline.won_value)}</p>
        </div>
        <div className="rounded-lg border px-3 py-2" data-testid="pipeline-avg">
          <div className="flex items-center gap-1 text-muted-foreground text-[11px] mb-0.5"><DollarSign className="w-3 h-3" />Avg Deal</div>
          <p className="text-lg font-bold">{formatCurrency(stats.pipeline.avg_deal_size)}</p>
        </div>
        <div className="rounded-lg border px-3 py-2" data-testid="pipeline-total">
          <div className="flex items-center gap-1 text-muted-foreground text-[11px] mb-0.5"><TrendingUp className="w-3 h-3" />Total Pipeline</div>
          <p className="text-lg font-bold">{formatCurrency(stats.pipeline.total_value)}</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all-leads">All Leads</TabsTrigger>
          <TabsTrigger value="aging_3" data-testid="tab-aging-3">
            Aging 3+ Days {stats.aging3Days > 0 && <Badge variant="outline" className="ml-2 text-orange-600">{stats.aging3Days}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="aging_7" data-testid="tab-aging-7">
            Aging 7+ Days {stats.aging7Days > 0 && <Badge variant="destructive" className="ml-2">{stats.aging7Days}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="follow_ups" data-testid="tab-follow-ups">
            <Bell className="w-4 h-4 mr-1" /> My Follow-ups
          </TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="tab-pipeline">
            <Columns3 className="w-4 h-4 mr-1" /> Pipeline
          </TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">
            <BarChart3 className="w-4 h-4 mr-1" /> Analytics
          </TabsTrigger>
          {canValidateLeads && (
            <TabsTrigger value="meta" data-testid="tab-meta-connect">
              <Facebook className="w-4 h-4 mr-1" /> Meta Connect
            </TabsTrigger>
          )}
          {isSuperAdmin && (
            <TabsTrigger value="deleted" data-testid="tab-deleted">
              <Trash2 className="w-4 h-4 mr-1" /> Deleted
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          {/* Filters - responsive, wrap on small screens */}
          <div className="flex flex-col md:flex-row gap-2 md:gap-4 flex-wrap">
            <SearchInput value={search} onChange={setSearch} placeholder="Search leads..." className="flex-1 min-w-[200px] max-w-md" data-testid="search-leads" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="filter-status"><SelectValue placeholder="Filter by status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {STATUSES.map(s => <SelectItem key={s} value={s}>{STATUS_LABELS[s] || s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="filter-source"><SelectValue placeholder="Filter by source" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                {SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={domainFilter} onValueChange={setDomainFilter}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="filter-domain"><SelectValue placeholder="Filter by domain" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Domains</SelectItem>
                {DOMAINS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="filter-assignee"><SelectValue placeholder="Filter by assignee" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Assignees</SelectItem>
                <SelectItem value="__me__" data-testid="filter-assignee-me">My leads{_user?.name ? ` (${_user.name})` : ''}</SelectItem>
                {(employees || []).map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {/* Date Range Filter - responsive */}
            <div className="flex items-center gap-2 flex-wrap">
              <CalendarRange className="w-4 h-4 text-muted-foreground" />
              <Input 
                type="date" 
                value={dateFromInput} 
                onChange={(e) => {
                  setDateFromInput(e.target.value);
                  e.target.blur(); // Close the date picker after selection
                }}
                className="w-32 md:w-36"
                placeholder="From"
                data-testid="date-from"
              />
              <span className="text-muted-foreground text-sm">to</span>
              <Input 
                type="date" 
                value={dateToInput} 
                onChange={(e) => {
                  setDateToInput(e.target.value);
                  e.target.blur(); // Close the date picker after selection
                }}
                className="w-32 md:w-36"
                placeholder="To"
                data-testid="date-to"
              />
              {loading && (dateFromInput || dateToInput) && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Filtering...</span>
                </div>
              )}
              {(dateFromInput || dateToInput) && !loading && (
                <Button variant="ghost" size="sm" onClick={clearDateFilter}>
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Bulk Action Bar */}
          {selectedLeadIds.size > 0 && (
            <Card className="border-primary/30 bg-primary/5" data-testid="bulk-action-bar">
              <CardContent className="p-3 flex items-center gap-3 flex-wrap">
                <span className="text-sm font-medium">{selectedLeadIds.size} lead{selectedLeadIds.size > 1 ? 's' : ''} selected</span>
                <Select value={bulkAction} onValueChange={setBulkAction}>
                  <SelectTrigger className="w-[200px] h-8" data-testid="bulk-action-select"><SelectValue placeholder="Choose action..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__header_status" disabled className="font-semibold text-xs text-muted-foreground">Change Status</SelectItem>
                    {STATUSES.map(s => <SelectItem key={`status:${s}`} value={`status:${s}`}>{STATUS_LABELS[s] || s}</SelectItem>)}
                    <SelectItem value="__header_assign" disabled className="font-semibold text-xs text-muted-foreground">Assign To</SelectItem>
                    {(employees || []).map(e => <SelectItem key={`assign:${e.id}`} value={`assign:${e.id}`}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={handleBulkAction} disabled={!bulkAction} data-testid="bulk-action-apply">Apply</Button>
                <Button size="sm" variant="ghost" onClick={() => { setSelectedLeadIds(new Set()); setBulkAction(''); }} data-testid="bulk-action-clear">Clear</Button>
              </CardContent>
            </Card>
          )}

          {/* Table with loading overlay */}
          <Card>
            <CardContent className="p-0 relative">
              {/* Loading overlay */}
              {loading && !initialLoad && (
                <div className="absolute inset-0 bg-background/50 backdrop-blur-[2px] z-10 flex items-start justify-center pt-20">
                  <div className="flex items-center gap-2 bg-background border rounded-lg px-4 py-2 shadow-lg">
                    <RefreshCw className="w-4 h-4 animate-spin text-primary" />
                    <span className="text-sm font-medium">Updating results...</span>
                  </div>
                </div>
              )}
              <DataTable data={items} columns={columns} sort={sort} onSort={setSort} onRowClick={handleLeadClick} emptyMessage="No leads found" emptyIcon={<Users className="h-12 w-12 text-muted-foreground" />} />
            </CardContent>
          </Card>
          
          {/* Pagination */}
          <Pagination 
            page={pagination.page}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            pageSize={pagination.pageSize}
            onPageChange={pagination.onPageChange}
            data-testid="leads-pagination"
          />
        </TabsContent>

        <TabsContent value="aging_3" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-orange-600 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Leads Aging 3-6 Days
              </CardTitle>
              <CardDescription>These leads need attention - no status change in 3+ days (includes Follow Up 1 & 2 with 3-day rule)</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <DataTable 
                data={agingSummary?.leads_3_days || []} 
                columns={columns} 
                sort={sort} 
                onSort={setSort} 
                onRowClick={handleLeadClick} 
                emptyMessage="No aging leads" 
                emptyIcon={<Clock className="h-12 w-12 text-muted-foreground" />} 
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="aging_7" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Leads Aging 7+ Days - Urgent
              </CardTitle>
              <CardDescription>Critical: These leads have been stagnant for over a week (includes Follow Up 3 with 7 working-day rule)</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <DataTable 
                data={agingSummary?.leads_7_days || []} 
                columns={columns} 
                sort={sort} 
                onSort={setSort} 
                onRowClick={handleLeadClick} 
                emptyMessage="No urgent aging leads" 
                emptyIcon={<AlertTriangle className="h-12 w-12 text-muted-foreground" />} 
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="follow_ups" className="space-y-4">
          {/* Follow-ups AI Summary */}
          {followUpsSummary && (
            <Alert className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
              <Sparkles className="h-4 w-4 text-purple-600" />
              <AlertTitle className="text-purple-800">AI Summary</AlertTitle>
              <AlertDescription className="text-purple-700">
                {followUpsSummary.summary}
              </AlertDescription>
            </Alert>
          )}

          {/* Follow-ups Stats */}
          {followUpsSummary && (
            <div className="grid grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold">{followUpsSummary.total}</p>
                  <p className="text-sm text-muted-foreground">Total</p>
                </CardContent>
              </Card>
              <Card className="border-red-200 bg-red-50">
                <CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold text-red-600">{followUpsSummary.overdue}</p>
                  <p className="text-sm text-red-600">Overdue</p>
                </CardContent>
              </Card>
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{followUpsSummary.today}</p>
                  <p className="text-sm text-blue-600">Today</p>
                </CardContent>
              </Card>
              <Card className="border-green-200 bg-green-50">
                <CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{followUpsSummary.this_week}</p>
                  <p className="text-sm text-green-600">This Week</p>
                </CardContent>
              </Card>
            </div>
          )}

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    My Follow-ups
                  </CardTitle>
                  <CardDescription>Scheduled follow-ups from your activities</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {/* Date Filter */}
                  <div className="flex items-center gap-2">
                    <CalendarRange className="w-4 h-4 text-muted-foreground" />
                    <Input 
                      type="date" 
                      className="w-36 h-8" 
                      value={followUpDateFrom} 
                      onChange={(e) => setFollowUpDateFrom(e.target.value)}
                      placeholder="From"
                    />
                    <span className="text-muted-foreground">to</span>
                    <Input 
                      type="date" 
                      className="w-36 h-8" 
                      value={followUpDateTo} 
                      onChange={(e) => setFollowUpDateTo(e.target.value)}
                      placeholder="To"
                    />
                    {(followUpDateFrom || followUpDateTo) && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => { setFollowUpDateFrom(''); setFollowUpDateTo(''); }}
                        className="h-8 px-2"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <Button variant="outline" size="sm" onClick={loadFollowUps} disabled={loadingFollowUps}>
                    <RefreshCw className={`w-4 h-4 mr-1 ${loadingFollowUps ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loadingFollowUps ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : (
                <DataTable 
                  data={followUps} 
                  columns={followUpColumns} 
                  onRowClick={(row) => handleLeadClick({ id: row.lead_id, name: row.lead_name })}
                  emptyMessage="No follow-ups scheduled" 
                  emptyIcon={<Bell className="h-12 w-12 text-muted-foreground" />} 
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pipeline Kanban Board */}
        <TabsContent value="pipeline" className="space-y-4">
          <CRMKanbanBoard
            leads={leads || []}
            onStatusChange={async (leadId, newStatus) => {
              try {
                await crmAPI.updateLead(leadId, { status: newStatus });
                toast.success(`Lead moved to ${STATUS_LABELS[newStatus] || newStatus}`);
                refresh();
                refreshAging();
                refreshDashboard();
              } catch (err) {
                toast.error('Failed to move lead');
              }
            }}
            onSelectLead={(lead) => { setSelectedLead(lead); setDetailOpen(true); }}
          />
        </TabsContent>

        {/* Analytics Dashboard */}
        <TabsContent value="analytics" className="space-y-4">
          <CRMAnalytics leads={leads || []} pipelineStats={dashboardStats?.pipeline} />
        </TabsContent>

        {/* Meta Connect Tab - Performance Engineer / Admin */}
        {canValidateLeads && (
          <TabsContent value="meta" className="space-y-4">
            <MetaLeadsTab />
          </TabsContent>
        )}

        {/* Deleted Leads Tab - Super Admin Only */}
        {isSuperAdmin && (
          <TabsContent value="deleted" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trash2 className="w-5 h-5" /> Deleted Leads
                </CardTitle>
                <CardDescription>Restore deleted leads back to the active list</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {loadingDeleted ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : deletedLeads.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Trash2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No deleted leads</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {deletedLeads.map((lead) => (
                      <div key={lead.id} className="flex items-center justify-between p-4 hover:bg-muted/50">
                        <div>
                          <p className="font-medium">{lead.name || 'Unnamed'}</p>
                          <p className="text-sm text-muted-foreground">
                            {lead.company} • {lead.phone || lead.email || 'No contact'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Deleted on {formatDateTime(lead.deleted_at)} by {lead.deleted_by_name || 'Unknown'}
                          </p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleRestoreLead(lead.id)}
                          data-testid={`restore-lead-${lead.id}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" /> Restore
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import Leads from Excel</DialogTitle>
            <DialogDescription>Upload an Excel file (.xlsx) with lead data</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Template Download */}
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div className="text-sm">
                <p className="font-medium">Need a template?</p>
                <p className="text-muted-foreground">Download sample format</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleDownloadTemplate} data-testid="download-template-btn">
                <Download className="w-4 h-4 mr-1" /> Template
              </Button>
            </div>

            {/* File Upload */}
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              
              {importing ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Importing leads...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="w-8 h-8 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Click to upload Excel file</p>
                  <Button onClick={() => fileInputRef.current?.click()} data-testid="select-file-btn">
                    Select File
                  </Button>
                </div>
              )}
            </div>

            {/* Import Result */}
            {importResult && (
              <Alert variant={importResult.details?.errors?.length > 0 ? "destructive" : "default"}>
                <AlertTitle>Import Complete</AlertTitle>
                <AlertDescription>
                  <div className="text-sm space-y-2">
                    {/* Summary */}
                    <div className="flex flex-wrap gap-4 py-2 border-b">
                      <p>Total rows: <span className="font-semibold">{importResult.summary?.total_rows || importResult.total || 0}</span></p>
                      <p className="text-green-600">✓ Imported: <span className="font-semibold">{importResult.summary?.imported || importResult.imported || 0}</span></p>
                      <p className="text-yellow-600">⊘ Duplicates: <span className="font-semibold">{importResult.summary?.skipped_duplicate || importResult.skipped || 0}</span></p>
                      {(importResult.summary?.failed > 0 || importResult.details?.errors?.length > 0) && (
                        <p className="text-red-600">✗ Failed: <span className="font-semibold">{importResult.summary?.failed || importResult.details?.errors?.length || 0}</span></p>
                      )}
                    </div>
                    
                    {/* Duplicate Details */}
                    {importResult.details?.duplicate_leads?.length > 0 && (
                      <div className="mt-2">
                        <p className="font-medium text-yellow-600 mb-1">Duplicates Skipped:</p>
                        <div className="max-h-32 overflow-y-auto text-xs bg-yellow-50 p-2 rounded">
                          {importResult.details.duplicate_leads.map((dup, i) => (
                            <p key={i} className="text-yellow-700">
                              Row {dup.row}: {dup.name} ({dup.phone}) - {dup.reason}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Error Details */}
                    {importResult.details?.errors?.length > 0 && (
                      <div className="mt-2">
                        <p className="font-medium text-red-600 mb-1">Errors:</p>
                        <div className="max-h-32 overflow-y-auto text-xs bg-red-50 p-2 rounded">
                          {importResult.details.errors.map((err, i) => (
                            <p key={i} className="text-red-700">
                              Row {err.row}: {err.name || 'Unknown'} - {err.error}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Success message */}
                    {importResult.message && (
                      <p className="text-muted-foreground mt-2">{importResult.message}</p>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Expected Columns */}
            <div className="text-sm">
              <p className="font-medium mb-2">Expected columns:</p>
              <div className="flex flex-wrap gap-1">
                {['name*', 'phone', 'email', 'company', 'source', 'status', 'notes', 'remarks', 'assigned_to'].map(col => (
                  <Badge key={col} variant="outline" className="text-xs">{col}</Badge>
                ))}
              </div>
              <p className="text-muted-foreground mt-2 text-xs">* Only name is required. Duplicates are skipped by phone/email.</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setImportDialogOpen(false); setImportResult(null); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Sheet */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="w-[400px] sm:w-[540px]">
          {selectedLead && (
            <>
              <SheetHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <SheetTitle>{selectedLead.name}</SheetTitle>
                    <SheetDescription>{selectedLead.company || 'No company'}</SheetDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-green-600 border-green-200 hover:bg-green-50"
                      onClick={() => { setCallLead(selectedLead); setCallDialogOpen(true); }}
                      data-testid="ai-call-detail-btn"
                    >
                      <PhoneCall className="w-4 h-4 mr-1" /> AI Call
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-green-600 border-green-200 hover:bg-green-50"
                      onClick={() => { 
                        setSelectedLeadForWhatsApp(selectedLead); 
                        setWhatsAppModalOpen(true); 
                      }}
                      disabled={!selectedLead?.phone}
                      data-testid="whatsapp-detail-btn"
                    >
                      <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleEditLead(selectedLead)} data-testid="edit-lead-detail-btn">
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    {(isSuperAdmin || isAdmin) && (
                      <Button variant="destructive" size="sm" onClick={() => setDeleteDialogOpen(true)} data-testid="delete-lead-btn">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </SheetHeader>
              <div className="mt-6 space-y-6">
                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><p className="text-muted-foreground">Email</p><p>{selectedLead.email || '-'}</p></div>
                  <div><p className="text-muted-foreground">Phone</p><p>{selectedLead.phone || '-'}</p></div>
                  <div><p className="text-muted-foreground">Source</p><p>{selectedLead.source || '-'}</p></div>
                  <div><p className="text-muted-foreground">Status</p><Badge variant={STATUS_COLORS[selectedLead.status] || 'outline'}>{STATUS_LABELS[selectedLead.status] || selectedLead.status}</Badge></div>
                  <div><p className="text-muted-foreground">Assignee</p><p>{selectedLead.assigned_to_name || '-'}</p></div>
                  <div>
                    <p className="text-muted-foreground">Deal Size</p>
                    <p className={`font-semibold ${selectedLead.estimated_deal_size ? 'text-emerald-700' : ''}`}>{formatCurrency(selectedLead.estimated_deal_size)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Lead Score</p>
                    <div className="flex items-center gap-1.5">
                      {(() => {
                        const grade = selectedLead.lead_grade || 'F';
                        const score = selectedLead.lead_score || 0;
                        const bg = grade === 'A' ? 'bg-green-600' : grade === 'B' ? 'bg-blue-600' : grade === 'C' ? 'bg-yellow-600' : grade === 'D' ? 'bg-orange-600' : 'bg-red-600';
                        const gradeInfo = {
                          A: { label: 'Hot Lead', desc: 'High priority — ready to convert. Follow up immediately.' },
                          B: { label: 'Warm Lead', desc: 'Good potential. Nurture with targeted follow-ups.' },
                          C: { label: 'Moderate', desc: 'Needs engagement. Schedule a discovery call.' },
                          D: { label: 'Cool Lead', desc: 'Low engagement. Re-qualify or deprioritize.' },
                          F: { label: 'Cold Lead', desc: 'Minimal activity. Consider archiving or re-targeting.' },
                        }[grade] || { label: grade, desc: '' };
                        return (
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge className={`${bg} text-white cursor-help`}>{grade} {score}/100</Badge>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="max-w-[280px] p-3 bg-slate-900 text-white rounded-lg shadow-xl">
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <span className="font-bold text-sm">{gradeInfo.label}</span>
                                    <span className="text-xs opacity-80">{score}/100</span>
                                  </div>
                                  <p className="text-xs leading-relaxed opacity-90">{gradeInfo.desc}</p>
                                  {selectedLead.score_factors?.length > 0 && (
                                    <div className="border-t border-white/20 pt-2 mt-2">
                                      <p className="text-[10px] font-semibold uppercase tracking-wide opacity-70 mb-1">Score Breakdown</p>
                                      <ul className="space-y-0.5">
                                        {selectedLead.score_factors.map((f, i) => (
                                          <li key={i} className="text-[11px] opacity-85 flex items-start gap-1">
                                            <span className="shrink-0 mt-0.5">•</span>{f}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  <div className="border-t border-white/20 pt-1.5 mt-1.5">
                                    <p className="text-[10px] opacity-60">A (80-100) &gt; B (60-79) &gt; C (40-59) &gt; D (20-39) &gt; F (0-19)</p>
                                  </div>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      })()}
                    </div>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Aging</p>
                    <p>{selectedLead.days_since_status_change || 0} days</p>
                  </div>
                </div>

                {/* Notes & Remarks */}
                {(selectedLead.notes || selectedLead.remarks) && (
                  <div className="space-y-2">
                    {selectedLead.remarks && (
                      <div>
                        <p className="text-muted-foreground text-sm">Remarks</p>
                        <p className="text-sm">{selectedLead.remarks}</p>
                      </div>
                    )}
                    {selectedLead.notes && (
                      <div>
                        <p className="text-muted-foreground text-sm">Notes</p>
                        <p className="text-sm">{selectedLead.notes}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Activities */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold">Activity Timeline</h4>
                    <Button size="sm" onClick={() => setActivityDialogOpen(true)} data-testid="log-activity-btn"><Plus className="w-4 h-4 mr-1" />Log Activity</Button>
                  </div>
                  <ScrollArea className="h-[300px]">
                    {activities.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">No activities yet</p>
                    ) : (
                      <div className="space-y-4">
                        {activities.map((activity, idx) => {
                          const type = ACTIVITY_TYPES.find(t => t.value === activity.activity_type);
                          const Icon = type?.icon || FileText;
                          return (
                            <div key={idx} className="flex gap-3 pb-4 border-b last:border-0">
                              <div className="p-2 bg-muted rounded-full h-fit"><Icon className="w-4 h-4" /></div>
                              <div className="flex-1">
                                <p className="font-medium text-sm">{type?.label || activity.activity_type}</p>
                                <p className="text-sm text-muted-foreground">{activity.description || '-'}</p>
                                {activity.next_follow_up && (
                                  <p className="text-xs text-blue-600 mt-1">
                                    <Bell className="w-3 h-3 inline mr-1" />
                                    Follow-up: {new Date(activity.next_follow_up).toLocaleDateString()}
                                  </p>
                                )}
                                <p className="text-xs text-muted-foreground mt-1">{formatDateTime(activity.created_at)}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </ScrollArea>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Add Activity Dialog */}
      <Dialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Log Activity</DialogTitle></DialogHeader>
          <form onSubmit={handleAddActivity} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Activity Type</Label>
              <Select value={activityForm.activity_type} onValueChange={(v) => setActivityForm({ ...activityForm, activity_type: v })}>
                <SelectTrigger data-testid="activity-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ACTIVITY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes / Description</Label>
              <Textarea 
                value={activityForm.description} 
                onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })} 
                placeholder="Enter activity details..."
                data-testid="activity-notes-input" 
              />
            </div>
            <div className="space-y-2">
              <Label>Next Follow-up Date (Optional)</Label>
              <Input 
                type="datetime-local"
                value={activityForm.next_follow_up} 
                onChange={(e) => setActivityForm({ ...activityForm, next_follow_up: e.target.value })} 
                data-testid="activity-followup-input" 
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setActivityDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-activity-btn">Save Activity</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Lead</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{selectedLead?.name}&quot;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteLead} data-testid="confirm-delete-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Lead Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Lead</DialogTitle>
            <DialogDescription>Update lead information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name <span className="text-red-500">*</span></Label>
                <Input 
                  value={editFormData.name} 
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })} 
                  data-testid="edit-lead-name-input" 
                />
              </div>
              <div className="space-y-2">
                <Label>Company</Label>
                <Input 
                  value={editFormData.company} 
                  onChange={(e) => setEditFormData({ ...editFormData, company: e.target.value })} 
                  data-testid="edit-lead-company-input" 
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input 
                  type="email" 
                  value={editFormData.email} 
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })} 
                  data-testid="edit-lead-email-input" 
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input 
                  value={editFormData.phone} 
                  onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })} 
                  data-testid="edit-lead-phone-input" 
                />
              </div>
              <div className="space-y-2">
                <Label>Source</Label>
                <Select value={editFormData.source} onValueChange={(v) => setEditFormData({ ...editFormData, source: v })}>
                  <SelectTrigger data-testid="edit-lead-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Domain</Label>
                <Select value={editFormData.domain || ''} onValueChange={(v) => setEditFormData({ ...editFormData, domain: v })}>
                  <SelectTrigger data-testid="edit-lead-domain-select"><SelectValue placeholder="Select domain" /></SelectTrigger>
                  <SelectContent>{DOMAINS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={editFormData.status} onValueChange={(v) => setEditFormData({ ...editFormData, status: v })}>
                  <SelectTrigger data-testid="edit-lead-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{STATUS_LABELS[s] || s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Assignee</Label>
                <Select value={editFormData.owner} onValueChange={(v) => setEditFormData({ ...editFormData, owner: v })}>
                  <SelectTrigger data-testid="edit-lead-owner-select"><SelectValue placeholder="Select assignee" /></SelectTrigger>
                  <SelectContent>{(employees || []).map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Estimated Deal Size (₹)</Label>
                <Input 
                  type="number" min="0" step="1000"
                  value={editFormData.estimated_deal_size || ''} 
                  onChange={(e) => setEditFormData({ ...editFormData, estimated_deal_size: e.target.value })} 
                  placeholder="e.g. 500000"
                  data-testid="edit-lead-deal-size-input" 
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Remarks <span className="text-red-500">*</span></Label>
              <Input 
                value={editFormData.remarks} 
                onChange={(e) => setEditFormData({ ...editFormData, remarks: e.target.value })} 
                placeholder="Add remarks (required)..." 
                data-testid="edit-lead-remarks-input" 
                required
                minLength={3}
              />
              {editFormData.remarks.length > 0 && editFormData.remarks.trim().length < 3 && (
                <p className="text-xs text-red-500">Remark must be at least 3 characters</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea 
                value={editFormData.notes} 
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })} 
                data-testid="edit-lead-notes-input" 
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-edit-lead-btn">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* AI Call Dialog */}
      <AICallDialog
        open={callDialogOpen}
        onOpenChange={setCallDialogOpen}
        lead={callLead}
        onCallLogged={async () => {
          if (callLead?.id) {
            try {
              const res = await crmAPI.getActivities(callLead.id);
              setActivities(res.data || []);
            } catch (_) { /* ignore */ }
          }
          refresh();
        }}
      />

      {/* WhatsApp Modal */}
      {whatsAppModalOpen && selectedLeadForWhatsApp && (
        <WhatsAppMessenger
          open={whatsAppModalOpen}
          onClose={() => {
            setWhatsAppModalOpen(false);
            setSelectedLeadForWhatsApp(null);
          }}
          lead={selectedLeadForWhatsApp}
          onSuccess={() => {
            // Refresh activities to show WhatsApp message sent
            if (selectedLeadForWhatsApp?.id) {
              crmAPI.getActivities(selectedLeadForWhatsApp.id)
                .then(res => setActivities(res.data || []))
                .catch(() => {});
            }
            // Don't refresh leads list - prevents messenger reload
            // Silent polling in messenger will handle any updates
          }}
        />
      )}
    </div>
  );
}


function StatTile({ label, value, icon: Icon, color = 'text-muted-foreground', bg = '', onClick, clickable = true, testId }) {
  return (
    <div
      className={`rounded-lg border px-2.5 py-2 transition-all ${bg} ${clickable ? 'cursor-pointer hover:shadow-md hover:scale-[1.02]' : ''}`}
      onClick={clickable ? onClick : undefined}
      data-testid={testId}
    >
      <div className={`flex items-center gap-1 ${color} text-[11px] mb-0.5 whitespace-nowrap`}>
        {Icon && <Icon className="w-3 h-3 flex-shrink-0" />}{label}
      </div>
      <p className={`text-lg font-bold ${color !== 'text-muted-foreground' ? color : ''}`}>{value}</p>
    </div>
  );
}
